import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Button, Card, Field } from '../../ui';
import { getMyClientRow, listBodyMetrics } from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { formatDate } from '../../shared/formatDate';
import { text } from '../../shared/text';
import { useInstance } from '../instance/InstanceProvider';
import { ClientGuard } from './ClientGuard';
import { AppBar, ClientLoading, ClientPage } from './ClientPage';
import { hasConsent, useChoices } from './consentChoices';
import { MeasurementChart } from './MeasurementChart';
import {
  MEASUREMENTS,
  change,
  formatMeasurement,
  fromRows,
  inRange,
  latest,
  logMeasurements,
  parseEntered,
  series,
  useDeviceMeasurements,
} from './measurements';
import type { Measurement, MeasurementId, MeasurementKind } from './measurements';
import { today } from './schedule';

/**
 * The client's own body, over time.
 *
 * ## Three refusals, and they are the screen
 *
 *   * **only the client records anything.** There is no coach-facing path to
 *     this data at all — no admin screen, no write for a coach in
 *     `queries.real.ts`, and `BodyMetricRow` has no author field, because a
 *     measurement with an author is a measurement somebody else could have
 *     taken;
 *   * **no photograph, anywhere.** There is no `<input type="file">`, no capture
 *     control and no upload on this screen or any screen it reaches;
 *   * **no verdict.** No target, no goal, no score, no comparison, and no colour
 *     that reads as success or failure. The two words this screen has for a
 *     number are "Último" and "Cambio" — see `MeasurementChart.tsx`, which is
 *     specified entirely by what it must not contain.
 *
 * ## It follows the consent, and its absence is explained
 *
 * A client who has not opted in never sees the feature: the profile screen does
 * not list it and this route says so plainly rather than rendering an empty
 * chart or a disabled form. Nothing is written for them, either — the entry form
 * does not exist to write from.
 *
 * ## What is hollow, said on the screen rather than discovered
 *
 * **There is no `body_metric` table.** What the client enters is kept on this
 * device by `./measurements.ts` and reaches nobody — not the coach, not a
 * server. On a fixtures build the history under the form is invented; on real
 * data it is empty until the client types something, and it is gone when they
 * change phone. The screen says exactly that, permanently, under the form.
 */
export function MeasurementsPage() {
  return (
    <ClientGuard>
      <Measurements />
    </ClientGuard>
  );
}

function Measurements() {
  const instance = useInstance();
  const choices = useChoices();
  const me = useQuery({ queryKey: ['my-client-row'], queryFn: () => getMyClientRow(supabase) });
  const stored = useQuery({ queryKey: ['body-metrics'], queryFn: () => listBodyMetrics(supabase) });
  const device = useDeviceMeasurements();

  if (me.isPending) return <ClientLoading />;

  if (!hasConsent('metrics', me.data, choices)) {
    return <MetricsOff slug={instance.slug} />;
  }

  const all = fromRows(stored.data ?? []);
  const withSomething = MEASUREMENTS.filter((kind) => series(kind.id, all, device).length > 0);

  return (
    <ClientPage>
      <AppBar backTo={`/c/${instance.slug}/me`} backLabel={text('client.measurements.back')} />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {text('client.measurements.title')}
      </h1>
      <p className="text-ink-soft mt-2 text-sm leading-relaxed">
        {text('client.measurements.intro', { coach: instance.coachDisplayName })}
      </p>

      <EntryForm />

      {stored.isPending ? (
        <p className="text-ink-muted mt-6 text-sm">{text('app.loading')}</p>
      ) : withSomething.length === 0 ? (
        <Card className="mt-6">
          <h2 className="text-ink font-display text-xl">
            {text('client.measurements.emptyTitle')}
          </h2>
          <p className="text-ink-soft mt-2 text-sm leading-relaxed">
            {text('client.measurements.emptyBody')}
          </p>
        </Card>
      ) : (
        withSomething.map((kind) => (
          <History key={kind.id} kind={kind} entries={series(kind.id, all, device)} />
        ))
      )}

      {/*
        The truth about this screen, under the thing that would otherwise imply
        the opposite. Not a footnote and not dismissible.
      */}
      <p className="text-ink-muted mt-6 text-xs leading-relaxed">
        {text('client.measurements.onThisDevice', { coach: instance.coachDisplayName })}
      </p>
    </ClientPage>
  );
}

/**
 * The form: the fixed set, all optional, one day at a time.
 *
 * It takes the whole set at once rather than one measurement at a time because
 * that is what a person does — they stand on the scale, pick up the tape, and
 * write down what they got. Anything left blank is not written.
 */
function EntryForm() {
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [savedOn, setSavedOn] = useState<string | null>(null);

  function save() {
    const parsed: Partial<Record<MeasurementId, number>> = {};
    for (const kind of MEASUREMENTS) {
      const raw = values[kind.id] ?? '';
      const value = parseEntered(raw);
      if (value === null) continue;
      if (Number.isNaN(value) || !inRange(kind, value)) {
        setError(text('client.measurements.badNumber'));
        return;
      }
      parsed[kind.id] = value;
    }

    if (Object.keys(parsed).length === 0) {
      setError(text('client.measurements.nothingEntered'));
      return;
    }

    // The day, not the instant. A weight belongs to a morning.
    const on = today();
    logMeasurements(parsed, on);
    setValues({});
    setError(null);
    setSavedOn(on);
  }

  return (
    <Card className="mt-6">
      <h2 className="text-ink font-display text-xl">{text('client.measurements.addTitle')}</h2>
      <p className="text-ink-soft mt-1 text-sm">{text('client.measurements.addHint')}</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {MEASUREMENTS.map((kind) => (
          <Field
            key={kind.id}
            label={text(kind.name)}
            suffix={kind.unit}
            inputMode="decimal"
            value={values[kind.id] ?? ''}
            onChange={(event) => {
              setValues({ ...values, [kind.id]: event.target.value });
            }}
          />
        ))}
      </div>

      {/*
        There is no control here to attach, capture or upload anything. That is
        not an omission to be filled in later: `client-me` forbids a photograph
        of any kind, and `mvp-discovery` records attachments as the way a health
        file comes back through the side door.
      */}

      <div className="mt-4">
        <Button variant="primary" onClick={save}>
          {text('client.measurements.save')}
        </Button>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-danger mt-2 text-sm">
          {error}
        </p>
      )}
      {savedOn === null ? null : (
        <p role="status" className="text-ink-soft mt-2 text-sm">
          {text('client.measurements.saved', { date: formatDate(savedOn) })}
        </p>
      )}
    </Card>
  );
}

/**
 * One measurement's history: the latest value, the change where there is one,
 * the line where there are two, and every entry in words.
 *
 * The list is the substance and the line is the illustration, not the other way
 * round — which is also what makes the one-measurement case ordinary rather than
 * a special case with an apology in it.
 */
function History({ kind, entries }: { kind: MeasurementKind; entries: readonly Measurement[] }) {
  const name = text(kind.name);
  const newest = latest(entries);
  const moved = change(entries);

  return (
    <section className="mt-6">
      <h2 className="text-ink font-display text-xl">{name}</h2>

      {newest === null ? null : (
        <p className="text-ink mt-1 text-sm">
          {`${text('client.measurements.latest')}: ${formatMeasurement(newest.value, kind.unit)} ${text(
            'client.measurements.onDate',
            { date: formatDate(newest.on) },
          )}`}
        </p>
      )}

      {/*
        A signed number in the ordinary ink. Never an arrow, never a colour, and
        never a word for the direction — `client-tone`, and the hi-fi's own
        "↘ -2.0" is exactly what is not ported.
      */}
      {moved === null ? (
        <p className="text-ink-muted mt-1 text-sm">{text('client.measurements.oneOnly')}</p>
      ) : (
        <p className="text-ink-soft mt-1 text-sm">
          {`${text('client.measurements.change')}: ${formatMeasurement(moved, kind.unit, true)}`}
        </p>
      )}

      <MeasurementChart kind={kind} entries={entries} name={name} />

      <ul aria-label={text('client.measurements.historyLabel', { name })} className="mt-3">
        {[...entries].reverse().map((entry) => (
          <li
            key={entry.id}
            className="border-border text-ink-soft flex justify-between border-b py-2 text-sm last:border-b-0"
          >
            <span>{formatDate(entry.on)}</span>
            <span className="text-ink">{formatMeasurement(entry.value, kind.unit)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The client who has not opted in.
 *
 * No form, no history, no chart — and an explanation rather than a disabled
 * control, because a client who cannot find a feature assumes it is broken. The
 * choice is offered where it lives, on the profile screen, which now exists.
 */
function MetricsOff({ slug }: { slug: string }) {
  return (
    <ClientPage>
      <AppBar backTo={`/c/${slug}/me`} backLabel={text('client.measurements.back')} />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {text('client.measurements.title')}
      </h1>

      <Card className="mt-4">
        <h2 className="text-ink font-display text-xl">{text('client.measurements.offTitle')}</h2>
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">
          {text('client.measurements.offBody')}
        </p>
        <p className="text-ink-soft mt-2 text-sm leading-relaxed">
          {text('client.measurements.offHow')}
        </p>
      </Card>

      <Link
        to={`/c/${slug}/me`}
        className="text-primary mt-6 inline-flex min-h-11 items-center text-sm font-medium underline-offset-2 hover:underline"
      >
        {text('client.measurements.back')}
      </Link>
    </ClientPage>
  );
}
